import { isEmptyString } from '@repo/shared/utils'

export type Indices = [number, number] // Start and end indices of a match

export type Matches<T extends string | number | symbol> = {
  [key in T]: Indices[]
}

export type SearchResult<T extends Record<string, unknown>> = {
  item: T
  matches: Matches<keyof T>
}[]

export function search<T extends Record<string, unknown>>(
  items: T[],
  query: string,
  keys: (keyof T)[], // e.g. ['title', 'content']
): SearchResult<T> {
  const trimmedQuery = query.trim()
  if (!trimmedQuery) {
    // If the query is empty, return all items without matches
    return items.map((item) => ({ item, matches: {} as Matches<keyof T> }))
  }
  return items
    .map((item) => {
      const matches: Matches<keyof T> = {} as Matches<keyof T>

      for (const key of keys) {
        const value = item[key]
        if (typeof value !== 'string') continue // Skip non-string values

        // Find matches for the query in the value
        const result = findMatches(value, trimmedQuery)

        if (result.indices.length === 0) continue // No matches for this key

        matches[key] = result.indices
      }
      return { item, matches }
    })
    .filter((scoredItem) => Object.keys(scoredItem.matches).length > 0)
}

function findMatches(
  string: string, // The string to search within
  query: string, // The query to search for
): { indices: Indices[] } {
  const indices: Indices[] = []
  if (isEmptyString(string) || isEmptyString(query)) return { indices }

  const normalizedString = normalizeText(string)
  const normalizedQuery = normalizeText(query)

  // Build a mapping from each character in the normalized string to its original index
  // The normToOrig array is used to keep track of which character in the original string corresponds to each character in the normalized string because normalization can change the length of the string.
  const normToOrig: number[] = []
  let normIdx = 0
  for (let i = 0; i < string.length; i++) {
    const normChar = normalizeText(String(string[i]))
    for (let j = 0; j < normChar.length; j++) {
      normToOrig[normIdx++] = i
    }
  }

  let startIndex = 0
  while (startIndex < normalizedString.length) {
    const matchIndex = normalizedString.indexOf(normalizedQuery, startIndex)
    if (matchIndex === -1) break
    const origStart = normToOrig[matchIndex]
    const origEnd = normToOrig[matchIndex + normalizedQuery.length - 1]
    if (origStart !== undefined && origEnd !== undefined) {
      indices.push([origStart, origEnd])
    }
    startIndex = matchIndex + normalizedQuery.length
  }
  return { indices }
}

export const normalizeText = (text: string) => {
  if (!text) return ''
  return (
    text
      .toLowerCase()
      .normalize('NFD')
      // Replace apostrophes with a standard single quote
      .replace(/[\u2018\u2019]/g, "'")
      // Remove accents
      .replace(/[\u0300-\u036f]/g, '')
  )
}
